import React from "react";
import { zip } from "fflate";
import type { AsyncZippable } from "fflate";

import * as types from "types";
import { downloadFile } from "services/cortevaChain/methods/downloadFile";
import type { ScopeFile } from "services/fileApi/types/ScopeFile";
import useGetAccessToken from "components/Login/useGetAccessToken";
import { findVariantPermalink } from "components/Message/FilePreviewModal";
import saveBlob from "utilities/saveBlob";
import splitFilename from "../utilities/splitFilename";

/**
 * Zip entry names for `filenames`, in order. A repeated name gets a counter
 * before its extension: `data.csv`, `data (2).csv`, `data (3).csv`. Names are
 * compared case-insensitively because macOS and Windows unzip that way.
 */
function uniqueEntryNames(filenames: string[]): string[] {
  const usedNames = new Set<string>();
  return filenames.map((filename) => {
    const { baseName, extension } = splitFilename(filename);
    let candidate = filename;
    let counter = 2;
    while (usedNames.has(candidate.toLowerCase())) {
      if (extension === null) {
        candidate = `${baseName} (${counter})`;
      } else {
        candidate = `${baseName} (${counter}).${extension}`;
      }
      counter += 1;
    }
    usedNames.add(candidate.toLowerCase());
    return candidate;
  });
}

function zipAsync(entries: AsyncZippable): Promise<Uint8Array<ArrayBuffer>> {
  return new Promise((resolve, reject) => {
    zip(entries, (error, archive) => {
      if (error) {
        reject(error);
      } else {
        resolve(archive);
      }
    });
  });
}

interface UseDownloadFilesArgs {
  chatThread: types.ChatThread;
}

/**
 * Downloads files for the file explorer (ADR 071). One file is saved as is.
 * Several are zipped in the browser and saved as `<thread name>.zip`. If any
 * fetch fails, nothing is saved.
 */
export default function useDownloadFiles({ chatThread }: UseDownloadFilesArgs) {
  const getAccessToken = useGetAccessToken();
  const [isDownloading, setIsDownloading] = React.useState(false);

  const downloadFiles = React.useCallback(
    async (files: ScopeFile[]) => {
      if (files.length === 0) return;

      setIsDownloading(true);
      try {
        // Check every file before fetching any, so a missing variant starts no downloads.
        const permalinks = files.map((file) => {
          const permalink = findVariantPermalink(file.variants, "original");
          if (permalink === null) {
            throw new Error(`${file.filename} has no original variant`);
          }
          return permalink;
        });
        const blobs = await Promise.all(
          permalinks.map((permalink) => downloadFile(permalink, getAccessToken))
        );

        if (files.length === 1) {
          saveBlob(blobs[0], files[0].filename);
          return;
        }

        const entryNames = uniqueEntryNames(files.map((file) => file.filename));
        const entries = Object.fromEntries(
          await Promise.all(
            blobs.map(
              async (blob, index): Promise<[string, Uint8Array]> => [
                entryNames[index],
                new Uint8Array(await blob.arrayBuffer()),
              ]
            )
          )
        );
        const archive = await zipAsync(entries);
        const zipName = chatThread.name.trim() || "files";
        saveBlob(
          new Blob([archive], { type: "application/zip" }),
          `${zipName}.zip`
        );
      } catch (error: unknown) {
        console.error("Couldn't download files", error);
      } finally {
        setIsDownloading(false);
      }
    },
    [chatThread.name, getAccessToken]
  );

  return { downloadFiles, isDownloading };
}
